import { Link } from "react-router-dom";

interface BlogTag {
  id: string;
  name: string;
  slug: string;
}

interface RelatedPost {
  id: string;
  title: string;
  slug: string;
  cover_image_url: string | null;
  published_at: string | null;
}

interface BlogSidebarProps {
  postTags: BlogTag[];
  allTags: BlogTag[];
  relatedPosts: RelatedPost[];
  recentPosts: RelatedPost[];
}

export function BlogSidebar({ postTags, allTags, relatedPosts, recentPosts }: BlogSidebarProps) {
  return (
    <aside className="space-y-8">
      {/* Post Tags */}
      {postTags.length > 0 && (
        <div className="border border-border p-5 bg-card">
          <h3 className="text-sm font-bold text-foreground mb-3">תגיות הכתבה</h3>
          <div className="flex flex-wrap gap-2">
            {postTags.map(tag => (
              <Link
                key={tag.id}
                to={`/blog?tag=${tag.slug}`}
                className="text-xs border border-primary/30 px-2.5 py-1 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                {tag.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* All Tags */}
      {allTags.length > 0 && (
        <div className="border border-border p-5 bg-card">
          <h3 className="text-sm font-bold text-foreground mb-3">כל התגיות</h3>
          <div className="flex flex-wrap gap-2">
            {allTags.map(tag => {
              const isActive = postTags.some(pt => pt.id === tag.id);
              return (
                <Link
                  key={tag.id}
                  to={`/blog?tag=${tag.slug}`}
                  className={`text-xs px-2.5 py-1 border transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border text-muted-foreground hover:border-primary/50 hover:text-primary'
                  }`}
                >
                  {tag.name}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <div className="border border-border p-5 bg-card">
          <h3 className="text-sm font-bold text-foreground mb-4">מאמרים קשורים</h3>
          <div className="space-y-4">
            {relatedPosts.map(rp => (
              <Link
                key={rp.id}
                to={`/blog/${rp.slug}`}
                className="flex gap-3 group"
              >
                <div className="w-16 h-16 flex-shrink-0 bg-muted overflow-hidden">
                  {rp.cover_image_url ? (
                    <img src={rp.cover_image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-muted" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2 leading-snug">
                    {rp.title}
                  </p>
                  {rp.published_at && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(rp.published_at).toLocaleDateString('he-IL', { month: 'short', day: 'numeric' })}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Recent Posts */}
      {recentPosts.length > 0 && (
        <div className="border border-border p-5 bg-card">
          <h3 className="text-sm font-bold text-foreground mb-4">פוסטים אחרונים</h3>
          <div className="space-y-4">
            {recentPosts.map(rp => (
              <Link
                key={rp.id}
                to={`/blog/${rp.slug}`}
                className="flex gap-3 group"
              >
                <div className="w-16 h-16 flex-shrink-0 bg-muted overflow-hidden">
                  {rp.cover_image_url ? (
                    <img src={rp.cover_image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-muted" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2 leading-snug">
                    {rp.title}
                  </p>
                  {rp.published_at && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(rp.published_at).toLocaleDateString('he-IL', { month: 'short', day: 'numeric' })}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}
